import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard, { formatViews } from "@/components/feature/ComicCard";
import SectionTitle from "@/components/feature/SectionTitle";
import { comics, getComicById } from "@/mocks/comics";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || "");
  const [reading, setReading] = useState(false);
  const [like, setLike] = useState(false);
  const [collect, setCollect] = useState(false);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 喵趣漫画网 | 养猫日常漫画免费在线看`;
    }
    setReading(false);
  }, [comic]);

  if (!comic) {
    return (
      <div className="bg-background-50 min-h-screen">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 md:px-6 pt-40 pb-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-heading text-2xl text-foreground-950">
            没有找到这部漫画
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            这部作品可能已下架或地址有误，去别处看看吧。
          </p>
          <button
            onClick={() => window.REACT_APP_NAVIGATE("/category")}
            className="mt-6 rounded-full bg-primary-500 text-background-50 text-sm px-6 py-2.5 whitespace-nowrap cursor-pointer"
          >
            返回全部漫画
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const related = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 5);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ComicSeries",
    name: comic.title,
    description: comic.description,
    genre: comic.category,
    author: { "@type": "Person", name: comic.author },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: comic.rating,
      bestRating: "5",
      ratingCount: comic.likes,
    },
  };

  return (
    <div className="bg-background-50 min-h-screen">
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* 面包屑 */}
      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-24 pb-2">
        <nav className="flex items-center gap-2 text-xs text-foreground-600 flex-wrap">
          <Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <Link to="/category" className="hover:text-primary-600 whitespace-nowrap">全部漫画</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <span className="text-foreground-950 font-medium whitespace-nowrap">{comic.title}</span>
        </nav>
      </div>

      {/* 头部信息 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-10">
        <div className="flex flex-col md:flex-row gap-6 md:gap-10">
          <div className="shrink-0 w-52 md:w-64 mx-auto md:mx-0">
            <img
              src={comic.cover}
              alt={`${comic.title} 养猫日常漫画封面`}
              className="w-full aspect-[4/5] object-cover rounded-2xl border border-background-200"
            />
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 flex-wrap">
              <span className="rounded-full bg-primary-500/10 text-primary-700 text-xs px-3 py-1">
                {comic.category}
              </span>
              <span className="rounded-full bg-accent-500/10 text-accent-700 text-xs px-3 py-1">
                {comic.status}
              </span>
              <span className="rounded-full bg-secondary-200/70 text-secondary-900 text-xs px-3 py-1">
                作者：{comic.author}
              </span>
            </div>
            <h1 className="mt-3 font-heading text-3xl md:text-4xl text-foreground-950">
              {comic.title}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">{comic.tagline}</p>

            <div className="mt-5 flex items-center justify-center md:justify-start gap-6 text-sm text-foreground-700">
              <span><i className="ri-fire-line text-primary-500 mr-1"></i>{formatViews(comic.views)}人气</span>
              <span><i className="ri-star-fill text-accent-500 mr-1"></i>{comic.rating.toFixed(1)}分</span>
              <span><i className="ri-bookmark-line mr-1"></i>{comic.collects}收藏</span>
            </div>

            <p className="mt-5 text-sm leading-relaxed text-foreground-700 text-left max-w-2xl mx-auto md:mx-0">
              {comic.description}
            </p>

            <div className="mt-6 flex items-center justify-center md:justify-start gap-3 flex-wrap">
              <button
                onClick={() => { setReading(true); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm px-7 py-3 whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-play-fill"></i> 立即阅读
              </button>
              <button
                onClick={() => setLike((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-full text-sm px-6 py-3 whitespace-nowrap transition-colors cursor-pointer border ${
                  like
                    ? "bg-primary-500 border-primary-500 text-background-50"
                    : "bg-background-100 border-background-200 text-foreground-700 hover:border-primary-300"
                }`}
              >
                <i className={`${like ? "ri-heart-fill" : "ri-heart-line"}`}></i>
                {like ? "已喜欢" : "喜欢"}
              </button>
              <button
                onClick={() => setCollect((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-full text-sm px-6 py-3 whitespace-nowrap transition-colors cursor-pointer border ${
                  collect
                    ? "bg-accent-500 border-accent-500 text-background-50"
                    : "bg-background-100 border-background-200 text-foreground-700 hover:border-accent-300"
                }`}
              >
                <i className={`${collect ? "ri-bookmark-fill" : "ri-bookmark-line"}`}></i>
                {collect ? "已收藏" : "收藏"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 章节列表 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
        <SectionTitle
          title="章节列表"
          subtitle={`最新更新：${comic.latestChapter}`}
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {comic.chapters.map((ch, idx) => (
            <button
              key={ch.id}
              onClick={() => setReading(true)}
              className="group flex items-center justify-between rounded-xl bg-background-100/70 border border-background-200 px-4 py-3 text-left hover:border-primary-300 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-500/10 text-primary-700 text-xs shrink-0">
                  {idx === 0 ? <i className="ri-flashlight-fill"></i> : idx + 1}
                </span>
                <span className="text-sm text-foreground-800 truncate group-hover:text-primary-600">
                  {ch.title}
                </span>
              </span>
              <span className="text-xs text-foreground-500 shrink-0 ml-2">{ch.updateTime}</span>
            </button>
          ))}
        </div>
      </section>

      {/* 阅读面板 */}
      {reading && (
        <div className="fixed inset-0 z-50 bg-background-50 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
            <div className="sticky top-0 z-10 bg-background-50/95 backdrop-blur border-b border-background-200 -mx-4 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <img src={comic.cover} alt={comic.title} className="w-9 h-12 rounded object-cover" />
                <div className="min-w-0">
                  <p className="font-heading text-base text-foreground-950 truncate">{comic.title}</p>
                  <p className="text-xs text-foreground-600">{comic.chapters[0].title}</p>
                </div>
              </div>
              <button
                onClick={() => setReading(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-800 cursor-pointer"
                aria-label="关闭阅读"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <p className="mt-6 text-sm text-foreground-600 leading-relaxed">
              这是《{comic.title}》的阅读预览页。本章讲述了{comic.description.slice(0, 40)}…
            </p>

            <div className="mt-5 space-y-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <img
                  key={i}
                  src="https://readdy.ai/api/search-image?query=Cute%20warm%20cat%20comic%20manga%20panel%20with%20orange%20tabby%20cat%20doing%20funny%20daily%20antics%20in%20a%20cozy%20home%2C%20soft%20cream%20and%20peach%20flat%20illustration%2C%20speech%20bubbles%2C%20heartwarming%20humorous%20style&width=1200&height=800&seq=page-panel-01&orientation=landscape"
                  alt={`${comic.title} 漫画内页 ${i + 1}`}
                  className="w-full rounded-2xl border border-background-200"
                />
              ))}
            </div>

            <div className="mt-8 flex items-center justify-center gap-3">
              <button
                onClick={() => setReading(false)}
                className="rounded-full bg-background-100 border border-background-200 text-foreground-700 text-sm px-6 py-2.5 whitespace-nowrap cursor-pointer"
              >
                上一话
              </button>
              <button
                onClick={() => setReading(false)}
                className="rounded-full bg-primary-500 text-background-50 text-sm px-6 py-2.5 whitespace-nowrap cursor-pointer"
              >
                下一话
              </button>
            </div>
            <p className="mt-3 text-center text-xs text-foreground-600">
              预览版内容 · 更多章节持续更新中
            </p>
          </div>
        </div>
      )}

      {/* 相关推荐 */}
      <section className="bg-background-100/60 border-y border-background-200">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <SectionTitle
            title="同类推荐"
            subtitle={`更多${comic.category}作品等你发现`}
            action={{ label: "全部作品", to: "/category" }}
          />
          {related.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {comics.slice(0, 5).map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}